import { Loader2 } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import { TransactionRow } from './TransactionRow'
import { TransactionRowExpanded } from './TransactionRowExpanded'
import type { Transaction } from './types'

interface TransactionListProps {
  transactions: Transaction[]
  isLoading: boolean
  clientId: string
  bankAccountId: number
  bankAccountLabel: string
  expandedRows: Set<string>
  onToggleRow: (txnId: string) => void
  selectedIds: Set<string>
  onToggleSelect: (txnId: string) => void
  onToggleSelectAll: () => void
}

export function TransactionList({
  transactions,
  isLoading,
  clientId,
  bankAccountId,
  bankAccountLabel,
  expandedRows,
  onToggleRow,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
}: TransactionListProps) {

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-neutral-400">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading transactions...
      </div>
    )
  }

  if (transactions.length === 0) {
    return (
      <div className="flex items-center justify-center py-20 text-neutral-400">
        No transactions found.
      </div>
    )
  }

  const visibleSelectedCount = transactions.filter((t) => selectedIds.has(t.id)).length
  const allSelected = visibleSelectedCount === transactions.length && transactions.length > 0
  const someSelected = visibleSelectedCount > 0 && !allSelected

  return (
    <div className="flex flex-col bg-white rounded-md border border-neutral-200">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-neutral-200 px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-neutral-500">
        <div className="flex h-4 w-4 shrink-0 items-center justify-center">
          <Checkbox
            checked={allSelected ? true : someSelected ? 'indeterminate' : false}
            onCheckedChange={() => onToggleSelectAll()}
            aria-label="Select all visible"
          />
        </div>
        <span className="w-4" />
        <span className="w-2.5" />
        <span className="w-[90px]">Date</span>
        <span className="flex-1">Counterparty</span>
        <span className="flex-1">Description</span>
        <span className="w-[120px] text-right">Amount</span>
      </div>

      {/* Rows */}
      <div className="divide-y divide-neutral-100">
        {transactions.map((txn) => (
          <div key={txn.id} id={`txn-${txn.id}`} data-txn-id={txn.id}>
            <TransactionRow
              transaction={txn}
              isExpanded={expandedRows.has(txn.id)}
              onToggle={() => onToggleRow(txn.id)}
              isSelected={selectedIds.has(txn.id)}
              onToggleSelect={() => onToggleSelect(txn.id)}
            />
            {expandedRows.has(txn.id) && (
              <TransactionRowExpanded
                transaction={txn}
                clientId={clientId}
                bankAccountId={bankAccountId}
                bankAccountLabel={bankAccountLabel}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
